import { useCallback } from 'react';
import shallow from 'zustand/shallow';

import tradingDataStore from 'lib/stores/tradingData';
import usePortfolioDetailStore from 'global/Securities/stores/usePortfolioDetailStore';
import useOrderDetailStore from 'global/Securities/stores/useOrderDetailStore';
import featureFlags from 'constants/featureFlags';
import useFeatureFlags from 'hooks/useFeatureFlags';
import type { OrderStateItem as OrderItem } from 'global/Securities/types/store/order';
import Transform from 'utils/securities/transformWebsocketData';

import useSecuritiesMessage from '../hooks/useSecuritiesMessage';

import { SecuritiesMessageCase, DATA_ACTION } from '../constants';

export default function usePortfolioReceiver() {
  const {
    updatePortfolioSummary,
    updatePortfolioList,
    updatePortfolioItem,
    deletePortfolioListItem,
    updateOrderList,
    updateOrderListItem,
    deleteOrderListItem,
  } = tradingDataStore(
    (s) => ({
      updatePortfolioSummary: s.updatePortfolioSummary,
      updatePortfolioList: s.updatePortfolioList,
      updatePortfolioItem: s.updatePortfolioItem,
      deletePortfolioListItem: s.deletePortfolioItem,
      updateOrderList: s.updateOrderList,
      updateOrderListItem: s.updateOrderListItem,
      deleteOrderListItem: s.deleteOrderListItem,
    }),
    shallow,
  );

  const { updatePortfolioDetail } = usePortfolioDetailStore(
    (s) => ({
      updatePortfolioDetail: s.updatePortfolioDetail,
    }),
    shallow,
  );

  const { updateOrderDetail } = useOrderDetailStore(
    (s) => ({
      updateOrderDetail: s.updateOrderDetail,
    }),
    shallow,
  );

  const handleReceiveMessage = useCallback(
    ({ action: messageAction, message }: SecuritiesResponse) => {
      if (!message) return;

      switch (message.case) {
        case SecuritiesMessageCase.SUMMARY: {
          const transformedData = Transform.portfolioSummary(
            message.value as PortfolioSummary,
          );

          updatePortfolioSummary(transformedData);
          break;
        }
        case SecuritiesMessageCase.PORTFOLIO_LIST: {
          const transformedData = Transform.portfolioList(
            message.value as PortfolioList,
          );

          updatePortfolioList(transformedData);
          break;
        }
        case SecuritiesMessageCase.PORTFOLIO_DETAIL: {
          const transformedData = Transform.portfolioItem(
            message.value as PortfolioDetail,
          );

          if (messageAction === DATA_ACTION.DELETE) {
            deletePortfolioListItem(transformedData.symbol);
            break;
          }

          updatePortfolioDetail(transformedData);
          updatePortfolioItem(transformedData.symbol, transformedData);
          break;
        }
        case SecuritiesMessageCase.PORTFOLIO_DETAIL_DAY_TRADE: {
          const transformedData = Transform.portfolioItem(
            message.value as PortfolioDetail,
          );

          if (messageAction === DATA_ACTION.DELETE) {
            deletePortfolioListItem(transformedData.symbol, true);
            break;
          }

          updatePortfolioDetail(transformedData, true, true);
          updatePortfolioItem(transformedData.symbol, transformedData, true);

          break;
        }
        case SecuritiesMessageCase.ORDER_LIST: {
          const transformedData = Transform.orderList(
            message.value as OrderList,
          );

          updateOrderList(transformedData);
          break;
        }
        case SecuritiesMessageCase.ORDER_DETAIL: {
          const transformedData = Transform.orderDetail(
            message.value as OrderDetail,
          );

          updateOrderListItem(transformedData.orderid, transformedData);
          updateOrderDetail(transformedData as OrderItem);

          if (messageAction === DATA_ACTION.DELETE) {
            deleteOrderListItem(transformedData.orderid);
          }
          break;
        }
        default:
          break;
      }
    },
    [],
  );

  useSecuritiesMessage('securities', handleReceiveMessage);
}
